// Top navigation bar for desktop
import { motion } from 'framer-motion';
import { Translate } from 'i18n';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileMenu from 'students/views/pages/Member/components/navigation/components/ProfileMenu';
import { useTypedDispatch, useTypedSelector } from 'students/stores/_utils';
import { articlesActions } from 'students/stores/articles/slice';
import { selectArticlesUnreadCount } from 'students/stores/articles/selectors';
import WhatsNewPanel from 'students/views/pages/Member/components/whatsNew/WhatsNewPanel';
import ProgressTeaser from 'students/views/pages/Member/components/home/components/ProgressTeaser';
import LinkElem from 'students/views/shared/components/LinkElem';
import { HeaderSupportDropdown } from 'students/views/shared/components/languagesSelection/LangDropdown';
import { useBreakPoint } from 'students/views/shared/hooks';
import { useFixedSurface } from 'students/views/shared/providers/Theme';
import { fixedSurfaceInk, onLightTheme } from 'students/views/shared/styled';
import userVariables from 'students/views/shared/styled/userVariables';
import styled from 'styled-components';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { navigationDesktopBreakPoint } from '../theme/breakpoints';
import GoToTeacherArea from './GoToTeacherArea';
import ChatIcon from './components/ChatIcon';
import whatsNewIcon from './assets/whats-new.svg';
import { INavigationItem, navigationMediaQuery } from './config';
import useNavBarItems from './hooks/useNavBarItems';
import { IconLink, SRoteLink } from './styles';
import useNavBar from './useNavBar';
import MobileChatIcon from './components/MobileChatIcon';

type tProps = {
  mobileVariant?: 'small' | 'none';
};
const SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME = 'memberSupportDropdownContent';
const HOME_PATH = '/';
const NavBar: FC<tProps> = ({ mobileVariant }) => {
  const { logoSrc, userProfile, handleLogOut, isNavigationVisible } = useNavBar();
  const isFixedSurface = useFixedSurface();
  const navBarItems = useNavBarItems();
  const isDesktop = useBreakPoint('xl');
  const location = useLocation();
  const dispatch = useTypedDispatch();
  const unreadCount = useTypedSelector(selectArticlesUnreadCount);

  if (!isNavigationVisible && mobileVariant === 'none') {
    return null;
  }

  const checkNavLinkIsActive =
    (item: INavigationItem) =>
    (match: Record<string, any> | null, location: Record<string, any>): boolean => {
      if (!match) {
        return false;
      }

      if (match.isExact && match.path.slice(1) === item.path) {
        return true;
      }

      if (location?.state?.isSubRoute && match.path.slice(1) === item.path) {
        return true;
      }

      return match.isExact;
    };

  const renderNavBarItem = (item: INavigationItem) => {
    const link = item.isDynamic ? (
      <motion.div
        key={`${item.name}_motion`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <SRoteLink
          isActive={checkNavLinkIsActive(item)}
          activeClassName="active"
          to={item.path}
        >
          {item.icon && <IconLink $src={item.icon} />}
          <Translate scope="frontend.mobile_tabs" str={item.name} />
        </SRoteLink>
      </motion.div>
    ) : (
      <SRoteLink
        isActive={checkNavLinkIsActive(item)}
        activeClassName="active"
        key={`${item.name}_link`}
        to={item.path}
      >
        {item.icon && <IconLink $src={item.icon} />}
        <Translate scope="frontend.mobile_tabs" str={item.name} />
      </SRoteLink>
    );

    return item.badgeCount ? (
      <SBadgeWrapper
        key={`${item.name}_badge`}
        $badgeCount={item.badgeCount}
        $isActive={item.path === location.pathname}
      >
        {link}
      </SBadgeWrapper>
    ) : (
      link
    );
  };

  return (
    <>
      <SNavBarWrapper $isFixedSurface={isFixedSurface}>
        <SNavBar>
          {isNavigationVisible ? (
            <>
              <SNavigation>
                <SLogoLink to="/">
                  <SLogo src={logoSrc} alt="Lingu" />
                </SLogoLink>

                {navBarItems.map(renderNavBarItem)}

                <ChatIcon />
              </SNavigation>

              {userProfile?.isTeacher && isDesktop ? <GoToTeacherArea /> : null}
            </>
          ) : null}

          {/* Small screens have no room for the home header's stats card, so the
              bar form takes over the empty start of this row instead. */}
          {!isNavigationVisible && userProfile && location.pathname === HOME_PATH ? (
            <SNavBarProgressTeaser variant="bar" />
          ) : null}

          <SProfileContainer>
            {isDesktop && (
              <>
                <button
                  type="button"
                  className="ln-flex ln-gap-[10px] ln-items-center ln-p-[10px] ln-rounded-[14px] ln-w-full ln-bg-transparent ln-border-0 ln-cursor-pointer hover:ln-bg-overlay/10 ln-transition-colors ln-duration-150"
                  onClick={() => dispatch(articlesActions.openPanel())}
                >
                  <UrlIcon
                    url={whatsNewIcon}
                    height="28px"
                    width="28px"
                    color="currentColor"
                    className="ln-text-icon-muted"
                  />
                  <span className="ln-flex-1 ln-text-content ln-text-[18px] ln-font-normal ln-tracking-[-0.36px] ln-whitespace-nowrap ln-text-start">
                    <Translate
                      str="frontend.whats_new.title"
                      params={{ defaultValue: "What's new?" }}
                    />
                  </span>
                  {unreadCount > 0 && (
                    <span className="ln-bg-[#c085ff] ln-h-[26px] ln-flex ln-items-center ln-justify-center ln-px-[6px] ln-rounded-[8px] ln-text-black ln-font-bold ln-text-[14px] ln-whitespace-nowrap ln-shrink-0 ln-lowercase">
                      <Translate
                        str="frontend.whats_new.new_tag"
                        params={{ defaultValue: 'new' }}
                      />
                    </span>
                  )}
                </button>
                <div className="ln-w-full ln-h-px ln-bg-overlay/10 ln-my-3" />
              </>
            )}

            {!isDesktop && <MobileChatIcon />}
            <SHeaderSupportDropdown
              direction="up"
              contentClassName={SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME}
              withIndicateError
            />

            <ProfileMenu userProfile={userProfile} handleLogOut={handleLogOut} />
          </SProfileContainer>
        </SNavBar>
      </SNavBarWrapper>
      <WhatsNewPanel />
    </>
  );
};

export default NavBar;

const SNavBarWrapper = styled.div<{ $isFixedSurface: boolean }>`
  width: 100%;
  padding-top: env(safe-area-inset-top);
  z-index: 1;

  ${({ $isFixedSurface }) =>
    $isFixedSurface &&
    onLightTheme`
      @media (max-width: ${navigationDesktopBreakPoint - 1}px) {
        ${fixedSurfaceInk}
        --navigation-navbar-background-color: rgba(0, 0, 0, 0.3);
      }
    `}

  ${navigationMediaQuery} {
    height: 80px;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    height: auto;
    width: auto;
    position: sticky;
  }
  display: flex;
`;

const SNavBar = styled.header`
  padding: 1rem;
  width: 100%;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 44px;

  a {
    text-decoration: none;
  }

  ${navigationMediaQuery} {
    height: 80px;
    padding: 0 2rem;
    background: var(--navigation-navbar-background-color);
    justify-content: space-between;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    position: sticky;
    top: 0;
    flex-direction: column;
    align-items: stretch;
    flex-shrink: 0;
    max-height: 100vh;
    height: initial;
    width: ${userVariables.sidebarNavigationWidth};
    padding: 40px 20px;
    background: var(--navigation-navbar-background-color);
    justify-content: space-between;

    /* Scrolling lives on SNavigation, not here: an overflow other than 'visible'
       on one axis forces the other axis to clip too, so the sidebar used to cut
       off the language dropdown that opens above the profile section. */
    overflow: visible;
  }
`;

/* Claims the start of the bar and yields every pixel the icon cluster needs:
   it is the only item here allowed to shrink. */
const SNavBarProgressTeaser = styled(ProgressTeaser)`
  margin-inline-end: auto;
  min-width: 0;
  flex: 0 1 auto;
`;

const SProfileContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 8px;
  flex-shrink: 0;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: block;
    margin-block-start: auto;

    & > *:first-child {
      margin-block-end: 16px;
    }
  }
`;

const SNavigation = styled.nav`
  ${navigationMediaQuery} {
    display: flex;
    align-items: center;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: block;
    /* Takes over the sidebar's scrolling so the links still collapse into a
       scroll area when they outgrow the viewport. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
`;

const SHeaderSupportDropdown = styled(HeaderSupportDropdown)`
  & .${SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME} {
    inset-inline-start: auto;
    inset-inline-end: -50px;
    transform: translateX(0) !important;

    @media (min-width: ${navigationDesktopBreakPoint}px) {
      inset-inline-start: 0 !important;
      transform: translateX(0) !important;
      top: auto !important;
      bottom: calc(100% + 4px) !important;
    }
  }

  @media screen and (min-width: 1200px) {
    margin-bottom: 0.75rem;

    & > button {
      border: none !important;
      border-radius: 16px !important;

      & > .button-inner {
        color: rgb(var(--ln-content)) !important;
        font-weight: 500 !important;

        & > span {
          font-size: 15px !important;
        }
      }
    }
  }
`;

const SBadgeWrapper = styled.div<{ $badgeCount?: number; $isActive: boolean }>`
  position: relative;

  ${({ $badgeCount, $isActive }) =>
    $badgeCount
      ? `
      a, button {
        padding-right: 38px;
      }

      &:after {
        content: '${$badgeCount}';
        display: flex;
        justify-content: center;
        align-items: center;
        width: 26px;
        height: 26px;
        background: ${$isActive ? 'rgb(11, 20, 32)' : 'rgb(var(--ln-badge-bg))'};
        border-radius: 8px;
        color: ${$isActive ? 'rgb(var(--ln-badge-active-ink))' : 'rgb(11, 20, 32)'};
        font-weight: 600;
        font-size: 15px;
        position: absolute;
        top: calc(50% - 13px);
        inset-inline-end: 10px;
      }
    `
      : ''}
`;

const SLogoLink = styled(LinkElem)`
  display: flex;
  flex-shrink: 0;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    margin-block-end: 32px;
  }

  &:focus {
    outline: 3px solid var(--color-blue-btn-focus-outline-color);
    outline-offset: 5px;
    border-radius: 15px;
  }
`;

const SLogo = styled.img`
  width: 4rem;
  height: auto;
  margin-inline-end: 0.5rem;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    margin-inline-end: 0;
    width: 6.75rem;
  }
`;
